import { useQuery } from '@apollo/client'
import { Box } from '@mui/material'
import { MutableRefObject } from 'react'
import { GetRosterCandidateRecruiterNotes } from 'recruiter/graphql/rosterProfile.gql'
import useToast from 'shared/components/Toast/useToast'
import RosterCandidateRecruiterNote, {
    RecruiterNoteData,
} from './RosterCandidateRecruiterNote'

type EditingNote = 'internal' | 'external' | null

interface RosterCandidateRecruiterNotesProps {
    isVisible?: boolean
    rosterCandidateId: string
    onSave?: () => void
    editingNote?: EditingNote
    onStartEdit?: (note: 'internal' | 'external') => void
    onCancelEdit?: () => void
    saveFnRef?: MutableRefObject<(() => void) | null>
}

const RosterCandidateRecruiterNotes = ({
    isVisible = true,
    rosterCandidateId,
    onSave,
    editingNote,
    onStartEdit,
    onCancelEdit,
    saveFnRef,
}: RosterCandidateRecruiterNotesProps) => {
    const toast = useToast()

    const {
        data: recruiterNotes,
        refetch,
        loading,
    } = useQuery(GetRosterCandidateRecruiterNotes, {
        variables: { rosterCandidateId: rosterCandidateId },
        onError: (error) => {
            console.error('Error fetching notes:', error)
            toast({
                title: 'Error',
                message: error.message,
                severity: 'error',
            })
        },
        skip: !isVisible,
    })

    const internalNoteData: RecruiterNoteData = {
        note:
            recruiterNotes?.rosterCandidate?.internalRecruiterNote?.note ?? '',
        updatedAt:
            recruiterNotes?.rosterCandidate?.internalRecruiterNote?.updatedAt ??
            null,
        updatedByUser: {
            preferredName:
                recruiterNotes?.rosterCandidate?.internalRecruiterNote
                    ?.updatedByUser?.preferredName ?? '',
        },
        createdByUser: {
            preferredName:
                recruiterNotes?.rosterCandidate?.internalRecruiterNote
                    ?.createdByUser?.preferredName ?? '',
        },
        createdAt:
            recruiterNotes?.rosterCandidate?.internalRecruiterNote?.createdAt ??
            null,
        canEdit:
            recruiterNotes?.rosterCandidate?.roster?.permissions?.canEdit ??
            false,
    }

    const externalNoteData: RecruiterNoteData = {
        note:
            recruiterNotes?.rosterCandidate?.externalRecruiterNote?.note ?? '',
        updatedAt:
            recruiterNotes?.rosterCandidate?.externalRecruiterNote?.updatedAt ??
            null,
        updatedByUser: {
            preferredName:
                recruiterNotes?.rosterCandidate?.externalRecruiterNote
                    ?.updatedByUser?.preferredName ?? '',
        },
        createdByUser: {
            preferredName:
                recruiterNotes?.rosterCandidate?.externalRecruiterNote
                    ?.createdByUser?.preferredName ?? '',
        },
        createdAt:
            recruiterNotes?.rosterCandidate?.externalRecruiterNote?.createdAt ??
            null,
        canEdit:
            recruiterNotes?.rosterCandidate?.roster?.permissions?.canEdit ??
            false,
    }

    const handleSave = async () => {
        await refetch()
        onSave?.()
    }

    return (
        <>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <RosterCandidateRecruiterNote
                    isLoading={loading}
                    noteData={internalNoteData}
                    onSave={() => {
                        handleSave()
                    }}
                    label={'Note for CPI'}
                    rosterCandidateId={rosterCandidateId}
                    isInternalNote={true}
                    {...(onStartEdit && {
                        isEditing: editingNote === 'internal',
                        onStartEdit: () => onStartEdit('internal'),
                        onCancelEdit,
                        editingDisabled: editingNote === 'external',
                        saveFnRef,
                    })}
                />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <RosterCandidateRecruiterNote
                    isLoading={loading}
                    noteData={externalNoteData}
                    onSave={() => {
                        handleSave()
                    }}
                    label={'Note for Client'}
                    rosterCandidateId={rosterCandidateId}
                    isInternalNote={false}
                    {...(onStartEdit && {
                        isEditing: editingNote === 'external',
                        onStartEdit: () => onStartEdit('external'),
                        onCancelEdit,
                        editingDisabled: editingNote === 'internal',
                        saveFnRef,
                    })}
                />
            </Box>
        </>
    )
}

export default RosterCandidateRecruiterNotes
